import { alpha, type Theme } from '@mui/material/styles'
import type { SystemStyleObject } from '@mui/system'

/**
 * Переопределения стилей Editor.js под тёмную тему.
 *
 * Своей тёмной темы у Editor.js нет: он рисует управление тёмным по светлому и
 * в тёмной теме кнопки плюса и настроек блока сливаются с фоном — учитель
 * находил их только наведением, когда всплывала подложка.
 *
 * Правила живут отдельным модулем и вешаются на холдер редактора через `sx`, а
 * не в глобальный CSS: так цвета берутся из токенов темы MUI, а не хардкодятся,
 * и переопределения не протекают на страницы, где Editor.js не смонтирован.
 * Селекторы Editor.js 2.31: панель блока и её попап рендерятся внутри холдера,
 * поэтому потомковых селекторов достаточно.
 *
 * В светлой теме возвращается пустой объект — её внешний вид не меняется.
 */
export function editorJsThemeSx(theme: Theme): SystemStyleObject<Theme> {
  if (theme.palette.mode !== 'dark') {
    return {}
  }

  const surface = theme.palette.background.paper
  const hovered = alpha(theme.palette.common.white, 0.08)
  const border = theme.palette.divider

  return {
    // Плюс и шестерёнка: тёмные по умолчанию, поэтому в тёмной теме их не было
    // видно без наведения.
    '& .ce-toolbar__plus, & .ce-toolbar__settings-btn': {
      color: theme.palette.text.primary,
      backgroundColor: 'transparent',
      border: `1px solid ${border}`,
    },
    '& .ce-toolbar__plus:hover, & .ce-toolbar__settings-btn:hover': {
      backgroundColor: hovered,
      color: theme.palette.text.primary,
    },

    // Меню выбора блока и меню настроек блока — один компонент попапа.
    '& .ce-popover, & .ce-popover__container': {
      backgroundColor: surface,
      borderColor: border,
      color: theme.palette.text.primary,
      boxShadow: theme.shadows[8],
    },
    '& .ce-popover-item__title, & .ce-popover-item__icon, & .ce-popover__items': {
      color: theme.palette.text.primary,
    },
    '& .ce-popover-item__icon': {
      backgroundColor: alpha(theme.palette.common.white, 0.06),
      boxShadow: 'none',
    },
    '& .ce-popover-item:hover:not(.ce-popover-item--no-hover)': {
      backgroundColor: hovered,
    },
    '& .ce-popover-item--active': {
      backgroundColor: alpha(theme.palette.primary.main, 0.24),
      color: theme.palette.primary.contrastText,
    },
    // Поиск по блокам внутри попапа: поле ввода тоже светлое по умолчанию.
    '& .cdx-search-field': {
      backgroundColor: alpha(theme.palette.common.white, 0.06),
      borderColor: border,
      color: theme.palette.text.primary,
    },
    '& .cdx-search-field__input': {
      color: theme.palette.text.primary,
      '&::placeholder': { color: theme.palette.text.disabled },
    },
    '& .cdx-search-field__icon svg': {
      color: theme.palette.text.secondary,
    },

    // Инлайн-панель форматирования выделенного текста.
    '& .ce-inline-toolbar': {
      backgroundColor: surface,
      borderColor: border,
      color: theme.palette.text.primary,
      boxShadow: theme.shadows[8],
    },
    '& .ce-inline-tool, & .ce-inline-toolbar__dropdown': {
      color: theme.palette.text.primary,
    },
    '& .ce-inline-tool:hover, & .ce-inline-toolbar__dropdown:hover': {
      backgroundColor: hovered,
    },
    '& .ce-inline-tool--active': {
      color: theme.palette.primary.light,
    },
    // Поле ссылки инлайн-панели.
    '& .ce-inline-tool-input': {
      backgroundColor: alpha(theme.palette.common.white, 0.06),
      color: theme.palette.text.primary,
      '&::placeholder': { color: theme.palette.text.disabled },
    },

    // Подсказка пустого блока: дефолтная слишком тёмная на тёмном фоне.
    '& .ce-paragraph[data-placeholder]:empty::before, & .ce-header[data-placeholder]:empty::before': {
      color: theme.palette.text.disabled,
    },

    // Выделение блока при переносе и при выборе — светлая заливка по умолчанию
    // делает текст нечитаемым.
    '& .ce-block--selected .ce-block__content': {
      backgroundColor: alpha(theme.palette.primary.main, 0.2),
    },
    '& .ce-toolbar__settings-btn--hidden': {
      opacity: 0,
    },
  }
}
